import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Seo from '../components/Seo';
import JsonLd from '../components/JsonLd';
import { useIsMobile } from '../components/ui/use-mobile';
import Footer from '../components/Footer';
import JobCard from '../components/JobCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Search, Filter, X, ChevronLeft, ChevronRight, ChevronDown, SlidersHorizontal } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { jobService } from '../services';
import { useBannerRotation } from '../hooks/useBannerRotation';
import type { Job, JobFilters } from '../types';

const AREAS = [
  { id: '1', label: 'Comercial/Vendas' },
  { id: '2', label: 'Administração' },
  { id: '3', label: 'Tecnologia' },
  { id: '4', label: 'Produção' },
  { id: '5', label: 'Logística' },
  { id: '6', label: 'Recursos Humanos' },
  { id: '7', label: 'Financeiro' },
  { id: '8', label: 'Marketing' },
  { id: '9', label: 'Atendimento' },
  { id: '10', label: 'Outros' },
];

const TYPES = [
  { value: 'clt', label: 'CLT' },
  { value: 'estagio', label: 'Estágio' },
  { value: 'temporario', label: 'Temporário' },
  { value: 'pj', label: 'Pessoa Jurídica' },
  { value: 'freelancer', label: 'Freelancer' },
];

const MODALITIES = [
  { value: 'presencial', label: 'Presencial' },
  { value: 'remoto', label: 'Remoto' },
  { value: 'hibrido', label: 'Híbrido' },
];

export default function JobListing() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Sistema de banners com rodízio por tipo
  const { normalBanners, pickFromPool, trackClick, trackImpression } = useBannerRotation({
    rotationInterval: 15000,
  });

  // Mesmo pool normal: topo pega 2, sidebar pega 1 (com offsets diferentes)
  const bannersTop = pickFromPool(normalBanners, 0, 2);
  const bannersSidebar = pickFromPool(normalBanners, 3, 1);

  // Registra impressões dos banners exibidos (uma vez por sessão de página)
  const seenImpressions = useRef<Set<number>>(new Set());
  useEffect(() => {
    [...bannersTop, ...bannersSidebar].forEach((b) => {
      if (b?.id && !seenImpressions.current.has(b.id)) {
        seenImpressions.current.add(b.id);
        trackImpression(b.id);
      }
    });
  }, [bannersTop, bannersSidebar, trackImpression]);

  // Função para construir URL completa da imagem do banner
  const getBannerImageUrl = (imageUrl: string) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) return imageUrl;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseUrl = ((import.meta as any).env?.VITE_API_URL as string)?.replace('/api', '') || window.location.origin;
    return `${baseUrl}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  };

  // Registra clique do banner
  const handleBannerClick = (banner: { id: number; link?: string }) => {
    trackClick(banner.id);
    if (banner.link) {
      window.open(banner.link, '_blank');
    }
  };

  // Estados dos filtros (inicializados da URL)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedAreas, setSelectedAreas] = useState<string[]>(
    searchParams.get('area') ? [searchParams.get('area')!] : []
  );
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    searchParams.get('type') ? [searchParams.get('type')!] : []
  );
  const [selectedModalities, setSelectedModalities] = useState<string[]>(
    searchParams.get('modality') ? [searchParams.get('modality')!] : []
  );
  const [isPcd, setIsPcd] = useState(searchParams.get('is_pcd') === 'true');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'recent');

  // Estados de dados
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const ITEMS_PER_PAGE = 12;

  // Carrega vagas com filtros
  const loadJobs = useCallback(async () => {
    try {
      setLoading(true);

      const filters: JobFilters = {
        page: currentPage,
        per_page: ITEMS_PER_PAGE,
      };

      // Termo de busca textual - só envia se tiver pelo menos 2 caracteres
      const trimmedSearch = searchQuery.trim();
      const hasValidSearch = trimmedSearch.length >= 2;

      if (hasValidSearch) {
        filters.search = trimmedSearch;
      }

      // Filtros específicos enviados como parâmetros separados
      if (selectedAreas.length > 0) {
        filters.area_id = Number(selectedAreas[0]);
      }

      if (selectedTypes.length > 0) {
        filters.type = selectedTypes[0];
      }

      if (selectedModalities.length > 0) {
        filters.modality = selectedModalities[0];
      }

      if (isPcd) {
        filters.is_pcd = true;
      }

      // Sempre usa o endpoint principal /jobs que suporta filtros e busca
      const response = await jobService.getJobs(filters);

      setJobs(response.data || []);
      setTotalPages(response.last_page || 1);
      setTotalJobs(response.total || 0);
    } catch (error) {
      console.error('Erro ao carregar vagas:', error);
      setJobs([]);
      setTotalPages(1);
      setTotalJobs(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, selectedAreas, selectedTypes, selectedModalities, isPcd]);

  // Atualiza URL com filtros
  const updateURL = useCallback(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedAreas.length > 0) params.set('area', selectedAreas[0]);
    if (selectedTypes.length > 0) params.set('type', selectedTypes[0]);
    if (selectedModalities.length > 0) params.set('modality', selectedModalities[0]);
    if (isPcd) params.set('is_pcd', 'true');
    if (currentPage > 1) params.set('page', String(currentPage));
    if (sortBy !== 'recent') params.set('sort', sortBy);

    setSearchParams(params);
  }, [searchQuery, selectedAreas, selectedTypes, selectedModalities, isPcd, currentPage, sortBy, setSearchParams]);

  // Carrega vagas quando filtros ou página mudam
  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Atualiza URL
  useEffect(() => {
    updateURL();
  }, [updateURL]);

  // Toggle de filtros de checkbox
  const toggleArea = (area: string) => {
    setSelectedAreas((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
    );
    setCurrentPage(1);
  };

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
    setCurrentPage(1);
  };

  const toggleModality = (modality: string) => {
    setSelectedModalities((prev) =>
      prev.includes(modality) ? prev.filter((m) => m !== modality) : [...prev, modality]
    );
    setCurrentPage(1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadJobs();
    // Scroll para o topo da listagem após busca
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedAreas([]);
    setSelectedTypes([]);
    setSelectedModalities([]);
    setIsPcd(false);
    setCurrentPage(1);
  };

  const hasActiveFilters =
    searchQuery ||
    selectedAreas.length > 0 ||
    selectedTypes.length > 0 ||
    selectedModalities.length > 0 ||
    isPcd;

  // Estados dos dropdowns de filtro
  const [openFilters, setOpenFilters] = useState<Record<string, boolean>>({
    area: false,
    type: false,
    modality: false,
    pcd: false,
  });

  const toggleFilter = (filter: string) => {
    setOpenFilters((prev) => ({ ...prev, [filter]: !prev[filter] }));
  };

  // Componente de filtro colapsável
  const FilterCollapsible = ({
    id,
    title,
    children,
    count = 0,
  }: {
    id: string;
    title: string;
    children: React.ReactNode;
    count?: number;
  }) => {
    const isOpen = openFilters[id] || false;

    return (
      <div>
        <button
          type="button"
          onClick={() => toggleFilter(id)}
          className="flex items-center justify-between w-full py-3 border-b border-gray-200 hover:bg-gray-50 transition-colors rounded-md px-2 -mx-2"
        >
          <span className="font-medium text-gray-900 text-sm">{title}</span>
          <div className="flex items-center gap-2">
            {count > 0 && (
              <span className="bg-brand-600 text-white text-xs rounded-full size-5 flex items-center justify-center">
                {count}
              </span>
            )}
            <ChevronDown
              className={`size-4 text-gray-500 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </div>
        </button>
        {isOpen && (
          <div className="pt-3 pb-1">
            {children}
          </div>
        )}
      </div>
    );
  };

  const FilterSection = () => (
    <div className="space-y-2">
      {/* Área de Interesse */}
      <FilterCollapsible id="area" title="Área de interesse" count={selectedAreas.length}>
        <div className="space-y-2">
          {AREAS.map((area) => (
            <div key={area.id} className="flex items-center space-x-2">
              <Checkbox
                id={`area-${area.id}`}
                checked={selectedAreas.includes(area.id)}
                onCheckedChange={() => toggleArea(area.id)}
              />
              <Label htmlFor={`area-${area.id}`} className="cursor-pointer text-sm text-gray-700">
                {area.label}
              </Label>
            </div>
          ))}
        </div>
      </FilterCollapsible>

      {/* Tipo de Contratação */}
      <FilterCollapsible id="type" title="Tipo de contratação" count={selectedTypes.length}>
        <div className="space-y-2">
          {TYPES.map((type) => (
            <div key={type.value} className="flex items-center space-x-2">
              <Checkbox
                id={`type-${type.value}`}
                checked={selectedTypes.includes(type.value)}
                onCheckedChange={() => toggleType(type.value)}
              />
              <Label htmlFor={`type-${type.value}`} className="cursor-pointer text-sm text-gray-700">
                {type.label}
              </Label>
            </div>
          ))}
        </div>
      </FilterCollapsible>

      {/* Modalidade */}
      <FilterCollapsible id="modality" title="Modalidade" count={selectedModalities.length}>
        <div className="space-y-2">
          {MODALITIES.map((modality) => (
            <div key={modality.value} className="flex items-center space-x-2">
              <Checkbox
                id={`modality-${modality.value}`}
                checked={selectedModalities.includes(modality.value)}
                onCheckedChange={() => toggleModality(modality.value)}
              />
              <Label htmlFor={`modality-${modality.value}`} className="cursor-pointer text-sm text-gray-700">
                {modality.label}
              </Label>
            </div>
          ))}
        </div>
      </FilterCollapsible>

      {/* Vaga PCD */}
      <FilterCollapsible id="pcd" title="Acessibilidade" count={isPcd ? 1 : 0}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="pcd"
            checked={isPcd}
            onCheckedChange={(checked: boolean | 'indeterminate') => {
              setIsPcd(checked === true);
              setCurrentPage(1);
            }}
          />
          <Label htmlFor="pcd" className="cursor-pointer text-sm text-gray-700">
            Apenas vagas PCD
          </Label>
        </div>
      </FilterCollapsible>

      {hasActiveFilters && (
        <Button variant="outline" className="w-full mt-4" onClick={clearFilters}>
          <X className="size-4 mr-2" />
          Limpar filtros
        </Button>
      )}
    </div>
  );

  const isMobile = useIsMobile();

  // Paginação
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = isMobile ? 3 : 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex flex-wrap items-center justify-center gap-2 mt-8 px-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setCurrentPage((p) => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="size-4" />
          <span className="hidden sm:inline ml-1">Anterior</span>
        </Button>

        {startPage > 1 && (
          <>
            <Button
              variant={currentPage === 1 ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCurrentPage(1)}
            >
              1
            </Button>
            {startPage > 2 && <span className="text-gray-400">...</span>}
          </>
        )}

        {pages.map((page) => (
          <Button
            key={page}
            variant={currentPage === page ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCurrentPage(page)}
          >
            {page}
          </Button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="text-gray-400">...</span>}
            <Button
              variant={currentPage === totalPages ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
            >
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => { setCurrentPage((p) => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          disabled={currentPage === totalPages}
        >
          <span className="hidden sm:inline mr-1">Próximo</span>
          <ChevronRight className="size-4" />
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="Vagas em Brusque - Encontre oportunidades"
        description="Busque vagas em Brusque por área, modalidade e tipo de contratação. Candidate-se de graça."
        canonical={window.location.origin + '/vagas'}
        ogImage={window.location.origin + '/logo-vagas.svg'}
      />
      <JsonLd
        id="jsonld-breadcrumb-vagas"
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: window.location.origin + '/' },
            { '@type': 'ListItem', position: 2, name: 'Vagas', item: window.location.origin + '/vagas' },
          ],
        }}
      />
      <Header />

      {/* Breadcrumb e Busca */}
      <div className="bg-white border-b py-6">
        <div className="container mx-auto px-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              <Link to="/" className="hover:text-brand-600">Home</Link> / Vagas
            </p>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Vagas em Brusque</h1>

          {/* Busca */}
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="size-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                type="text"
                placeholder="Buscar por cargo, empresa ou palavra-chave..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit">Buscar</Button>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="lg:hidden">
                  <Filter className="size-4 mr-2" />
                  Filtros
                  {hasActiveFilters && (
                    <span className="ml-2 bg-brand-600 text-white text-xs rounded-full size-5 flex items-center justify-center">
                      !
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Filtrar vagas</SheetTitle>
                  <SheetDescription>Refine sua busca com os filtros abaixo</SheetDescription>
                </SheetHeader>
                <div className="mt-6 px-4">
                  <FilterSection />
                </div>
              </SheetContent>
            </Sheet>
          </form>
        </div>
      </div>

      {/* Banners Publicitários - Topo (jobs-top) */}
      {bannersTop.length > 0 && (
        <section className="py-6 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className={`grid grid-cols-1 ${bannersTop.length > 1 ? 'md:grid-cols-2' : ''} gap-4`}>
              {bannersTop.map((banner, index) => (
                <div
                  key={`jobs-top-${banner.id}-${index}`}
                  className={`rounded-xl overflow-hidden cursor-pointer bg-gray-100 ${index > 0 ? 'hidden md:block' : ''}`}
                  onClick={() => handleBannerClick(banner)}
                >
                  <img
                    src={getBannerImageUrl(banner.image_url)}
                    alt={banner.title}
                    className="w-full h-full object-contain transition-transform duration-300 hover:scale-[1.03]"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Conteúdo Principal */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filtros Desktop */}
          <aside className="hidden lg:block">
            <div className="bg-white rounded-lg border p-6 sticky top-24">
              <div className="flex items-center justify-between mb-4 pb-4 border-b-2 border-brand-200">
                <div className="flex items-center gap-2">
                  <div className="bg-brand-600 p-2 rounded-lg">
                    <SlidersHorizontal className="size-5 text-white" />
                  </div>
                  <h2 className="font-bold text-gray-900 text-lg">Filtros</h2>
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-brand-600 hover:underline font-medium"
                  >
                    Limpar
                  </button>
                )}
              </div>
              <FilterSection />
            </div>

            {/* Banner Sidebar (jobs-sidebar) */}
            {bannersSidebar.length > 0 && (
              <div className="mt-4 space-y-4">
                {bannersSidebar.map((banner, index) => (
                  <div
                    key={`jobs-sidebar-${banner.id}-${index}`}
                    className="rounded-xl overflow-hidden cursor-pointer bg-gray-100"
                    onClick={() => handleBannerClick(banner)}
                  >
                    <img
                      src={getBannerImageUrl(banner.image_url)}
                      alt={banner.title}
                      className="w-full h-auto object-cover transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            )}
          </aside>

          {/* Lista de Vagas */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-600">
                {loading ? (
                  'Carregando...'
                ) : (
                  <>
                    Exibindo <span className="font-medium text-gray-900">{jobs.length}</span> de{' '}
                    <span className="font-medium text-gray-900">{totalJobs}</span> vagas
                  </>
                )}
              </p>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Mais recentes</SelectItem>
                  <SelectItem value="salary">Maior salário</SelectItem>
                  <SelectItem value="views">Mais visualizadas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tags de filtros ativos */}
            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2 mb-4">
                {searchQuery && (
                  <span className="inline-flex items-center gap-1 bg-brand-100 text-brand-800 text-sm px-3 py-1 rounded-full">
                    Busca: {searchQuery}
                    <button onClick={() => setSearchQuery('')}>
                      <X className="size-3" />
                    </button>
                  </span>
                )}
                {selectedAreas.map((areaId) => (
                  <span
                    key={areaId}
                    className="inline-flex items-center gap-1 bg-brand-100 text-brand-800 text-sm px-3 py-1 rounded-full"
                  >
                    {AREAS.find((a) => a.id === areaId)?.label}
                    <button onClick={() => toggleArea(areaId)}>
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
                {selectedTypes.map((type) => (
                  <span
                    key={type}
                    className="inline-flex items-center gap-1 bg-brand-100 text-brand-800 text-sm px-3 py-1 rounded-full"
                  >
                    {TYPES.find((t) => t.value === type)?.label}
                    <button onClick={() => toggleType(type)}>
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
                {selectedModalities.map((modality) => (
                  <span
                    key={modality}
                    className="inline-flex items-center gap-1 bg-brand-100 text-brand-800 text-sm px-3 py-1 rounded-full"
                  >
                    {MODALITIES.find((m) => m.value === modality)?.label}
                    <button onClick={() => toggleModality(modality)}>
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
                {isPcd && (
                  <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
                    PCD
                    <button onClick={() => setIsPcd(false)}>
                      <X className="size-3" />
                    </button>
                  </span>
                )}
              </div>
            )}

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto mb-4" />
                <p className="text-gray-500">Carregando vagas...</p>
              </div>
            ) : jobs.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {jobs.map((job, index) => {
                  const elements = [<JobCard key={job.id} job={job} compact />];
                  // Insere banner intercalado a cada 6 vagas
                  if ((index + 1) % 6 === 0 && normalBanners.length > 0) {
                    const bannerIndex = Math.floor(index / 6) % normalBanners.length;
                    const inlineBanner = normalBanners[bannerIndex];
                    if (inlineBanner) {
                      elements.push(
                        <div
                          key={`inline-banner-${inlineBanner.id}-${index}`}
                          className="col-span-1 md:col-span-2 xl:col-span-3 rounded-xl overflow-hidden cursor-pointer bg-gray-100"
                          onClick={() => handleBannerClick(inlineBanner)}
                        >
                          <img
                            src={getBannerImageUrl(inlineBanner.image_url)}
                            alt={inlineBanner.title}
                            className="w-full h-auto object-contain transition-transform duration-300 hover:scale-[1.01]"
                          />
                        </div>
                      );
                    }
                  }
                  return elements;
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg border">
                <p className="text-gray-500 mb-4">Nenhuma vaga encontrada com os filtros selecionados.</p>
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearFilters}>
                    Limpar filtros
                  </Button>
                )}
              </div>
            )}

            {renderPagination()}

            {/* Banners Publicitários - Rodapé (reutiliza jobs-top) */}
            {bannersTop.length > 0 && (
              <div className={`grid grid-cols-1 ${bannersTop.length > 1 ? 'md:grid-cols-2' : ''} gap-4 mt-8`}>
                {bannersTop.map((banner, index) => (
                  <div
                    key={`jobs-bottom-${banner.id}-${index}`}
                    className={`rounded-xl overflow-hidden cursor-pointer bg-gray-100 ${index > 0 ? 'hidden md:block' : ''}`}
                    onClick={() => handleBannerClick(banner)}
                  >
                    <img
                      src={getBannerImageUrl(banner.image_url)}
                      alt={banner.title}
                      className="w-full h-full object-contain transition-transform duration-300 hover:scale-[1.03]"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
